import { useMemo } from 'react';
import { useQuery } from 'react-query';
import type { SearchApiOptions } from 'types/api';
import type { Metadata, SearchResultsBase } from 'types/search';

export function useSearchApi<
  TData,
  TFacetFields extends string,
  TResults extends SearchResultsBase<TData, TFacetFields>
>({
  keepPreviousData = true,
  queryKey,
  request,
  ...options
}: SearchApiOptions<TData, TFacetFields, TResults>) {
  const {
    data,
    isFetched,
    isFetching,
    isLoading,
    isPreviousData,
    ...response
  } = useQuery<TResults, unknown>(queryKey, request, {
    keepPreviousData,
    ...options,
  });

  const facet = useMemo(() => {
    return data?.metadata?.facet ?? ({} as Metadata<TFacetFields>['facet']);
  }, [data?.metadata?.facet]);

  return {
    ...response,
    ...data,
    facets: (Object.keys(facet) as TFacetFields[]).map((key) => ({
      ...facet[key],
      key,
    })),
    hasResults: !!data?.results?.[0] || isPreviousData,
    isFetched,
    isFetching,
    isLoading,
    isPreviousData,
    results: data?.results ?? [],
    total: data?.total ?? 0,
  };
}
